import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  Box,
  Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { getOrganizationDetails, updateBillingAddress } from '../services/organizationService';
import CountrySelect from './CountrySelect';

const BillingAddressEditor = ({ organizationId }) => {
  const [loading, setLoading] = useState(false);
  const [billingAddress, setBillingAddress] = useState({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });
  const [sameAsCompanyAddress, setSameAsCompanyAddress] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [error, setError] = useState('');

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadOrganizationData();
  }, [organizationId]);

  const loadOrganizationData = async () => {
    try {
      const orgData = await getOrganizationDetails();
      setOrganization(orgData);
      
      // Load existing billing address
      if (orgData?.billing?.billingAddress) {
        setBillingAddress(orgData.billing.billingAddress);
      }
      
      // Check if billing address matches company address
      if (orgData?.companyAddress && orgData?.billing?.billingAddress) {
        const addressMatch = 
          orgData.companyAddress.line1 === orgData.billing.billingAddress.line1 &&
          orgData.companyAddress.line2 === orgData.billing.billingAddress.line2 &&
          orgData.companyAddress.city === orgData.billing.billingAddress.city &&
          orgData.companyAddress.state === orgData.billing.billingAddress.state &&
          orgData.companyAddress.postal_code === orgData.billing.billingAddress.postal_code &&
          orgData.companyAddress.country === orgData.billing.billingAddress.country;
        setSameAsCompanyAddress(addressMatch);
      }
    } catch (error) {
      console.error('Error loading organization data:', error);
      setError('Failed to load organization data');
    }
  };

  // Handle "same as company address" checkbox
  useEffect(() => {
    if (sameAsCompanyAddress && organization?.companyAddress) {
      setBillingAddress(organization.companyAddress);
    }
  }, [sameAsCompanyAddress, organization]);

  const handleBillingAddressChange = (e) => {
    const { name, value } = e.target;
    setBillingAddress(prev => ({
      ...prev,
      [name]: value
    }));
    // Uncheck "same as company address" if user manually edits
    if (sameAsCompanyAddress) {
      setSameAsCompanyAddress(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await updateBillingAddress(organizationId, billingAddress);
      enqueueSnackbar('Billing address updated successfully!', { variant: 'success' });
      await loadOrganizationData(); // Refresh data
    } catch (error) {
      console.error('Error updating billing address:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update billing address';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Billing Address
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          This address is used for billing and invoicing purposes.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSave}>
          {organization?.companyAddress && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={sameAsCompanyAddress}
                  onChange={(e) => setSameAsCompanyAddress(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Same as company address"
              sx={{ mb: 2 }}
            />
          )}

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                name="line1"
                value={billingAddress.line1}
                onChange={handleBillingAddressChange}
                required
                placeholder="Street address"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2 (Optional)"
                name="line2"
                value={billingAddress.line2}
                onChange={handleBillingAddressChange}
                placeholder="Apartment, suite, etc."
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={billingAddress.city}
                onChange={handleBillingAddressChange}
                required
                placeholder="City"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="State/Province"
                name="state"
                value={billingAddress.state}
                onChange={handleBillingAddressChange}
                placeholder="State"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Postal Code"
                name="postal_code"
                value={billingAddress.postal_code}
                onChange={handleBillingAddressChange}
                placeholder="ZIP/Postal code"
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <CountrySelect
                value={billingAddress.country}
                onChange={(value) => {
                  setBillingAddress(prev => ({ ...prev, country: value }));
                  if (sameAsCompanyAddress) {
                    setSameAsCompanyAddress(false);
                  }
                }}
                required
                disabled={loading || sameAsCompanyAddress}
              />
            </Grid>
          </Grid>

          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Billing Address'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BillingAddressEditor; 